import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

export interface WarrantyData {
  customer: {
    name: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  project: {
    name: string;
    address?: string;
    projectAddress?: string;
  };
  product: {
    name: string;
    sku?: string;
  };
  deliveryInvoice?: {
    date?: string;
    createdAt?: string;
  };
  brand?: string;
  model?: string;
  type?: string;
  compressorType?: string;
  serialNo?: string;
  serviceWarrantyYears?: number;
  sparePartsWarrantyYears?: number;
  compressorWarrantyYears?: number;
  warrantyYears?: number;
  brandLogo?: string;
}

/**
 * Generate clean HTML content for warranty card printing
 */
export const generateWarrantyCardHTML = (warranty: WarrantyData, qrDataUrl: string, settings?: any, isFullDoc = true): string => {
  const company = settings?.company || {};
  
  // Barcode Generation
  const canvas = document.createElement('canvas');
  let barcodeDataUrl = '';
  try {
    JsBarcode(canvas, warranty.serialNo || 'N/A', {
      format: "CODE128",
      width: 1.0,
      height: 15,
      displayValue: false,
      margin: 0
    });
    barcodeDataUrl = canvas.toDataURL();
  } catch (e) {
    console.error('Barcode generation failed', e);
  }

  const formatPurchaseDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return '—';
    }
  };

  const purchaseDate = warranty.deliveryInvoice?.date 
    ? formatPurchaseDate(warranty.deliveryInvoice.date) 
    : formatPurchaseDate(warranty.deliveryInvoice?.createdAt);

  const warrantyItems = [
    { label: 'Service Warranty', val: warranty.serviceWarrantyYears },
    { label: 'Spare Parts Warranty', val: warranty.sparePartsWarrantyYears },
    { label: 'Compressor Warranty', val: warranty.compressorWarrantyYears },
    { label: 'General Warranty', val: warranty.warrantyYears }
  ].filter(w => w.val && w.val > 0);

  const getBrandLogoUrl = (brandName: string, backendLogoPath?: string): string => {
    const localLogos: Record<string, string> = {
      'Fujiaire': '/Fujiaire.png',
      'Hitachi': '/Hitachi.png',
      'Dunham-Bush': '/Dunham-Bush.png'
    };

    const normalizedName = Object.keys(localLogos).find(
      name => brandName.toLowerCase().includes(name.toLowerCase())
    );

    if (normalizedName) {
      return localLogos[normalizedName];
    }

    if (backendLogoPath) {
      return `${process.env.REACT_APP_API_URL || ''}${backendLogoPath}`;
    }

    return '';
  };

  const brandLogoUrl = getBrandLogoUrl(warranty.brand || '', warranty.brandLogo);

  const serialNo = warranty.serialNo || 'N/A';

  const content = `
      <div class="warranty-card-container">
        <div style="height: 4px; width: 100%; flex-shrink: 0; background: linear-gradient(90deg, #003d99, #0066cc, #003d99);"></div>
        
        <header>
          <div class="logo-box">
            ${brandLogoUrl ? `<img src="${brandLogoUrl}" alt="Logo">` : '<span style="font-size: 7px; color: #ccc; text-align: center;">BRAND<br>LOGO</span>'}
          </div>
          <div class="title-container">
            <h1>Warranty Card</h1>
          </div>
          <div class="shield-container">
            <div class="shield-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="m9 12 2 2 4-4"></path></svg>
            </div>
          </div>
        </header>

        <div class="content">
          <!-- Customer Details -->
          <section>
            <div class="section-header">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0066cc" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              <h2>Customer Details</h2>
            </div>
            <div class="data-grid">
              <div class="data-row">
                <div class="data-item">
                  <p class="data-label">Customer Name</p>
                  <p class="data-value">${warranty.customer.name}</p>
                </div>
                <div class="data-item">
                  <p class="data-label">Project Name</p>
                  <p class="data-value">${warranty.project.name}</p>
                </div>
              </div>
              <div class="data-item">
                <p class="data-label">Customer Address</p>
                <p class="data-value">${warranty.customer.address || warranty.project.address || warranty.project.projectAddress || '—'}</p>
              </div>
              <div class="data-row">
                <div class="data-item">
                  <p class="data-label">Contact No</p>
                  <p class="data-value">${warranty.customer.phone || '—'}</p>
                </div>
                <div class="data-item">
                  <p class="data-label">Purchase Date</p>
                  <p class="data-value">${purchaseDate}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Product Details -->
          <section>
            <div class="section-header">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0066cc" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m7.5 4.27 9 5.15"></path><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path><path d="m3.3 7 8.7 5 8.7-5"></path><path d="M12 22V12"></path></svg>
              <h2>Product Details</h2>
            </div>
            <div class="data-grid">
              <div class="data-item">
                <p class="data-label">Product Name</p>
                <p class="data-value" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${warranty.product.name}</p>
              </div>
              <div class="data-row">
                <div class="data-item">
                  <p class="data-label">Brand</p>
                  <p class="data-value">${warranty.brand || '—'}</p>
                </div>
                <div class="data-item">
                  <p class="data-label">Model</p>
                  <p class="data-value">${warranty.model || '—'}</p>
                </div>
              </div>
              <div class="data-row">
                <div class="data-item">
                  <p class="data-label">Type</p>
                  <p class="data-value">${warranty.type || '—'}</p>
                </div>
                <div class="data-item">
                  <p class="data-label">Compressor Type</p>
                  <p class="data-value">${warranty.compressorType || '—'}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Provider -->
          <section>
            <div class="section-header">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0066cc" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>
              <h2>Warranty Provider</h2>
            </div>
            <div class="provider-box">
              <div>
                <p class="data-label">Company</p>
                <p class="data-value">${company.name || 'The Aircons Ltd.'}</p>
              </div>
              <div>
                <p class="data-label">Address</p>
                <p class="data-value">${company.address || 'Plot-14, Main Road, Block-C, Banasree, Rampura, Dhaka-1219.'}</p>
              </div>
              <div class="data-row">
                <div class="data-item" style="border: none;">
                  <p class="data-label">Email</p>
                  <p class="data-value">${company.email || 'support@theaircons.com'}</p>
                </div>
                <div class="data-item" style="border: none;">
                  <p class="data-label">Phone</p>
                  <p class="data-value">${company.phone || '+880-1749-121607'}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Coverage -->
          <section>
            <div class="section-header">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0066cc" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect><line x1="16" x2="16" y1="2" y2="6"></line><line x1="8" x2="8" y1="2" y2="6"></line><line x1="3" x2="21" y1="10" y2="10"></line></svg>
              <h2>Warranty Coverage</h2>
            </div>
            <div class="coverage-grid">
              ${warrantyItems.map(w => `
                <div class="coverage-item">
                  <p class="coverage-val">${w.val} ${w.val === 1 ? 'Year' : 'Years'}</p>
                  <p class="coverage-label">${w.label}</p>
                </div>
              `).join('')}
            </div>
          </section>

          <!-- Signature Section -->
          <div class="signature-section">
            <div class="barcode-container">
              <p class="sl-text">SL: ${serialNo}</p>
              <img src="${barcodeDataUrl}" class="barcode-img" />
            </div>
            <div class="bottom-row">
              <div class="qr-placeholder">
                <img src="${qrDataUrl}" class="qr-img" />
                <p class="qr-label">Track Warranty</p>
              </div>
              <div class="signatory-box">
                <div class="sign-line"></div>
                <p class="sign-label">Authorized Signatory</p>
              </div>
            </div>
          </div>
        </div>
        <footer>
          <p>This warranty certificate is valid subject to company terms & condition.</p>
        </footer>
      </div>
  `;

  if (!isFullDoc) return content;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Warranty Card - ${serialNo}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: white;
          font-family: 'DM Sans', sans-serif;
          width: 4in;
          height: 6in;
        }

        .warranty-card-container {
          width: 4in;
          height: 6in;
          margin: 0 !important;
          padding: 0 !important;
          background: white;
          position: relative;
          display: flex;
          flex-direction: column;
          line-height: 1.1;
          color: #1e293b;
          overflow: hidden;
        }

        .page-break {
          page-break-after: always;
        }

        header {
          padding: 10px 16px 6px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #003d99;
          flex-shrink: 0;
        }

        .logo-box {
          width: 56px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          overflow: hidden;
          background: white;
        }

        .logo-box img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .title-container {
          flex: 1;
          text-align: center;
        }

        .title-container h1 {
          font-family: 'Playfair Display', serif;
          font-size: 16px;
          font-weight: bold;
          color: #0066cc;
          margin: 0;
          letter-spacing: -0.025em;
        }

        .shield-container {
          width: 56px;
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }

        .shield-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0066cc, #003d99);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }

        .content {
          padding: 8px 20px;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
          overflow: hidden;
        }

        section {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 2px;
        }

        .section-header h2 {
          font-size: 8px;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #0066cc;
          margin: 0;
        }

        .data-grid {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .data-row {
          display: flex;
          gap: 16px;
        }

        .data-item {
          flex: 1;
          border-bottom: 1px solid #003d99;
          padding-bottom: 2px;
        }

        .data-label {
          font-size: 8px;
          color: #003d99;
          text-transform: uppercase;
          font-weight: 700;
          letter-spacing: 0.025em;
          margin: 0;
        }

        .data-value {
          font-size: 8px;
          color: #1e293b;
          font-weight: 600;
          margin: 0;
        }

        .provider-box {
          border-radius: 8px;
          padding: 8px;
          background-color: white;
          border: 1px solid #000080;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .coverage-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .coverage-item {
          flex: 1;
          min-width: 60px;
          border-radius: 8px;
          padding: 4px;
          text-align: center;
          background-color: white;
          border: 1px solid #000080;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .coverage-val {
          font-family: 'Playfair Display', serif;
          font-size: 11px;
          font-weight: bold;
          color: #0066cc;
          margin: 0;
        }

        .coverage-label {
          font-size: 7px;
          font-weight: bold;
          text-transform: uppercase;
          color: #003d99;
          margin: 0;
          line-height: 1.1;
          word-break: break-word;
        }

        .signature-section {
          padding-top: 4px;
          border-top: 1px solid #003d99;
          display: flex;
          flex-direction: column;
          gap: 2px;
          margin-top: auto;
        }

        .barcode-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .barcode-img {
          height: 20px !important;
          max-width: 140px;
        }

        .sl-text {
          font-size: 8px;
          color: #003d99;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          font-weight: bold;
          margin: 0;
        }

        .bottom-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-top: -4px;
        }

        .qr-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-left: 8px;
        }

        .qr-img {
          width: 80px !important;
          height: 80px !important;
          margin-bottom: 2px;
        }

        .qr-label {
          font-size: 7px;
          font-weight: bold;
          text-transform: uppercase;
          color: #003d99;
          margin: 0;
        }

        .signatory-box {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          margin-right: 8px;
        }

        .sign-line {
          width: 110px;
          border-bottom: 1px solid #003d99;
          height: 30px;
          margin-bottom: 2px;
        }

        .sign-label {
          font-size: 7px;
          font-weight: bold;
          text-transform: uppercase;
          color: #003d99;
          margin: 0;
        }

        footer {
          padding: 2px 0;
          text-align: center;
          border-top: 1px solid #003d99;
          background-color: white;
          flex-shrink: 0;
        }

        footer p {
          font-size: 7px;
          color: #003d99;
          font-style: italic;
          margin: 0;
        }
@media print {
  @page {
    size: 4in 6in;
    margin: 0mm !important;
  }
  html, body {
    margin: 0mm !important;
    padding: 0mm !important;
    width: 4in !important;
    height: 6in !important;
    overflow: hidden !important;
  }
  .warranty-card-container {
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    width: 4in !important;
    height: 6in !important;
    margin: 0 !important;
    padding: 0 !important;
  }}
      </style>
    </head>
    <body>
      ${content}
      <script>
      window.onload = function() {
        const images = document.getElementsByTagName('img');
        let loadedCount = 0;
        
        if (images.length === 0) {
          window.print();
          return;
        }

        const checkLoaded = () => {
          loadedCount++;
          if (loadedCount === images.length) {
            setTimeout(() => {
              window.print();
            }, 500);
          }
        };

        for (let i = 0; i < images.length; i++) {
          if (images[i].complete) {
            checkLoaded();
          } else {
            images[i].onload = checkLoaded;
            images[i].onerror = checkLoaded;
          }
        }
      };
      </script>
    </body>
    </html>
  `;
};

/**
 * Print warranty card using clean HTML generation in a new window
 */
export const printWarrantyCard = async (warranty: WarrantyData, settings?: any) => {
  try {
    const serialNo = warranty.serialNo || 'N/A';
    const portalUrl = `https://theairconsims.netlify.app/CSPortal/Warranty/${serialNo}`;
    
    // Pre-generate QR Code
    const qrDataUrl = await QRCode.toDataURL(portalUrl, {
      width: 300,
      margin: 1,
      color: {
        dark: '#003d99',
        light: '#ffffff'
      }
    });

    const cleanHTML = generateWarrantyCardHTML(warranty, qrDataUrl, settings);
    
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    
    if (printWindow) {
      printWindow.document.write(cleanHTML);
      printWindow.document.close();
    } else {
      alert('Please allow popups to print the warranty card.');
    }
  } catch (error) {
    console.error('Failed to prepare print:', error);
    alert('Failed to prepare warranty card for printing.');
  }
};

/**
 * Print all warranty cards for an invoice
 */
export const printAllWarrantyCards = async (warranties: WarrantyData[], settings?: any) => {
  try {
    if (!warranties || warranties.length === 0) {
      alert('No warranties found to print.');
      return;
    }

    let allContent = '';
    
    for (let i = 0; i < warranties.length; i++) {
      const warranty = warranties[i];
      const serialNo = warranty.serialNo || 'N/A';
      const portalUrl = `https://theairconsims.netlify.app/CSPortal/Warranty/${serialNo}`;
      
      const qrDataUrl = await QRCode.toDataURL(portalUrl, {
        width: 300,
        margin: 1,
        color: {
          dark: '#003d99',
          light: '#ffffff'
        }
      });

      const cardContent = generateWarrantyCardHTML(warranty, qrDataUrl, settings, false);
      const isLast = i === warranties.length - 1;
      
      allContent += `
        <div class="${isLast ? '' : 'page-break'}">
          ${cardContent}
        </div>
      `;
    }

    const fullHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Batch Print Warranty Cards</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white;
            font-family: 'DM Sans', sans-serif;
            width: 100%;
            height: auto;
          }

          .warranty-card-container {
            width: 4in;
            height: 6in;
            margin: 0 auto !important;
            padding: 0 !important;
            background: white;
            position: relative;
            display: flex;
            flex-direction: column;
            line-height: 1.1;
            color: #1e293b;
            overflow: hidden;
            box-sizing: border-box;
          }

          .page-break {
            page-break-after: always;
          }

          header {
            padding: 10px 16px 6px 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid #003d99;
            flex-shrink: 0;
          }

          .logo-box {
            width: 56px;
            height: 40px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            overflow: hidden;
            background: white;
          }

          .logo-box img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }

          .title-container {
            flex: 1;
            text-align: center;
          }

          .title-container h1 {
            font-family: 'Playfair Display', serif;
            font-size: 16px;
            font-weight: bold;
            color: #0066cc;
            margin: 0;
            letter-spacing: -0.025em;
          }

          .shield-container {
            width: 56px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
          }

          .shield-icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: linear-gradient(135deg, #0066cc, #003d99);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          }

          .content {
            padding: 8px 20px;
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
            overflow: hidden;
          }

          section {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .section-header {
            display: flex;
            align-items: center;
            gap: 4px;
            margin-bottom: 2px;
          }

          .section-header h2 {
            font-size: 8px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            color: #0066cc;
            margin: 0;
          }

          .data-grid {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .data-row {
            display: flex;
            gap: 16px;
          }

          .data-item {
            flex: 1;
            border-bottom: 1px solid #003d99;
            padding-bottom: 2px;
          }

          .data-label {
            font-size: 8px;
            color: #003d99;
            text-transform: uppercase;
            font-weight: 700;
            letter-spacing: 0.025em;
            margin: 0;
          }

          .data-value {
            font-size: 8px;
            color: #1e293b;
            font-weight: 600;
            margin: 0;
          }

          .provider-box {
            border-radius: 8px;
            padding: 8px;
            background-color: white;
            border: 1px solid #000080;
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .coverage-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
          }

          .coverage-item {
            flex: 1;
            min-width: 60px;
            border-radius: 8px;
            padding: 4px;
            text-align: center;
            background-color: white;
            border: 1px solid #000080;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .coverage-val {
            font-family: 'Playfair Display', serif;
            font-size: 11px;
            font-weight: bold;
            color: #0066cc;
            margin: 0;
          }

          .coverage-label {
            font-size: 7px;
            font-weight: bold;
            text-transform: uppercase;
            color: #003d99;
            margin: 0;
            line-height: 1.1;
            word-break: break-word;
          }

          .signature-section {
            padding-top: 4px;
            border-top: 1px solid #003d99;
            display: flex;
            flex-direction: column;
            gap: 2px;
            margin-top: auto;
          }

          .barcode-container {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .barcode-img {
            height: 20px !important;
            max-width: 140px;
          }

          .sl-text {
            font-size: 8px;
            color: #003d99;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            font-weight: bold;
            margin: 0;
          }

          .bottom-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: -4px;
          }

          .qr-placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-left: 8px;
          }

          .qr-img {
            width: 80px !important;
            height: 80px !important;
            margin-bottom: 2px;
          }

          .qr-label {
            font-size: 7px;
            font-weight: bold;
            text-transform: uppercase;
            color: #003d99;
            margin: 0;
          }

          .signatory-box {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            margin-right: 8px;
          }

          .sign-line {
            width: 110px;
            border-bottom: 1px solid #003d99;
            height: 30px;
            margin-bottom: 2px;
          }

          .sign-label {
            font-size: 7px;
            font-weight: bold;
            text-transform: uppercase;
            color: #003d99;
            margin: 0;
          }

          footer {
            padding: 2px 0;
            text-align: center;
            border-top: 1px solid #003d99;
            background-color: white;
            flex-shrink: 0;
          }

          footer p {
            font-size: 7px;
            color: #003d99;
            font-style: italic;
            margin: 0;
          }

          @media print {
            @page {
              size: 4in 6in;
              margin: 0mm !important;
            }
            html, body {
              margin: 0mm !important;
              padding: 0mm !important;
              width: 4in !important;
              height: auto !important;
              overflow: visible !important;
            }
            .warranty-card-container {
              border: none !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              width: 4in !important;
              height: 6in !important;
              margin: 0 !important;
              padding: 0 !important;
              page-break-inside: avoid;
            }
            .page-break {
              page-break-after: always !important;
              break-after: page !important;
              display: block;
              width: 4in;
              height: 6in;
            }
          }
        </style>
      </head>
      <body>
        ${allContent}
        <script>
        window.onload = function() {
          const images = document.getElementsByTagName('img');
          let loadedCount = 0;
          
          if (images.length === 0) {
            window.print();
            return;
          }

          const checkLoaded = () => {
            loadedCount++;
            if (loadedCount === images.length) {
              setTimeout(() => {
                window.print();
              }, 1000);
            }
          };

          for (let i = 0; i < images.length; i++) {
            if (images[i].complete) {
              checkLoaded();
            } else {
              images[i].onload = checkLoaded;
              images[i].onerror = checkLoaded;
            }
          }
        };
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=600,height=800');
    
    if (printWindow) {
      printWindow.document.write(fullHTML);
      printWindow.document.close();
    } else {
      alert('Please allow popups to print warranty cards.');
    }
  } catch (error) {
    console.error('Failed to prepare batch print:', error);
    alert('Failed to prepare warranty cards for printing.');
  }
};
